import { css, styled } from 'canoe';
import React, { useRef } from 'react';

import { useOffsetFromViewport } from 'craft/hooks';
import { useRefresh } from 'craft/hooks/useRefresh';
import { useViewportDom } from 'craft/state/viewport/selectors';
import { getDomNodeFromDocument, isDomElement, isSourceElement } from 'craft/utils/viewport';

import { NodeLabelMenu } from './NodeLabelMenu';

const NodeLabelContainer = styled.div<{ top: number; left: number; hide?: boolean }>`
  ${({ top, left, hide }) => css`
    z-index: 2;
    position: absolute;
    top: ${top}px;
    left: ${left}px;
    opacity: ${hide ? '0' : '1'};
  `}
`;

export const NODE_LABEL_CONTAINER_ID = 'node-label-container';

export const setNodeLabelVisibility = (visible: boolean) => {
  const nodeLabelContainers = Array.from(
    document.querySelectorAll(`[id^="${NODE_LABEL_CONTAINER_ID}`),
  ) as HTMLElement[];
  if (nodeLabelContainers.length) {
    nodeLabelContainers
      .filter((dom) => isDomElement(dom))
      .forEach((nodeLabel) => {
        // eslint-disable-next-line no-param-reassign
        nodeLabel.style.opacity = visible ? '1' : '0';
      });
  }
};

export const NodeLabel = React.memo(({ nodeId }: { nodeId: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const dom: HTMLElement | null = getDomNodeFromDocument(nodeId);

  const { top, left, scrollTop, scrollLeft, documentOffsetLeft } = useOffsetFromViewport(dom, ref);
  const { viewportWrapperEl } = useViewportDom();

  /**
   * Unfortunately, sometimes the menu positioning is incorrect.
   *
   * The issue here is that we're rendering using plain js,
   * but the react tree might not be done rendering yet.
   * to get around that, we use a refresh function that upon complete render,
   * forces a rerender of the menu position.
   */
  const [refresh] = useRefresh();

  const hasActiveEditableAncestor = !!document.querySelector(
    `[data-slate-editor=true][data-nid^="${nodeId}"]`,
  );

  if (!isDomElement(dom) || !document.contains(dom) || hasActiveEditableAncestor) {
    return null;
  }

  const menuBounds = ref.current?.getBoundingClientRect();
  const isMappedOverSource = isSourceElement(dom);

  const computeLeft = () => {
    const componentRightEdge =
      left + documentOffsetLeft - scrollLeft + dom.offsetWidth - (menuBounds?.width ?? 0);

    const viewportRightEdge =
      (viewportWrapperEl?.clientWidth ?? componentRightEdge) - (menuBounds?.width ?? 0);

    // if entire node is visible or we have no ref to get measurements, keep label on right edge of node
    if (!viewportWrapperEl || viewportRightEdge > componentRightEdge) {
      return componentRightEdge;
    }

    // allow node label to move left within the visible area, but no less than the left edge of the node
    return Math.max(
      left - scrollLeft,
      componentRightEdge - Math.max(0, componentRightEdge - viewportRightEdge),
    );
  };

  const offsets = {
    // top position of the node minus height of the LabelMenu,
    // if it overflows the top of viewport keep stuck on top of edge
    top: Math.max(top - scrollTop, 0) - (menuBounds?.height ?? 0),
    // left position of the node minus size of the node minus width of the LabelMenu
    left: computeLeft(),
  };

  /**
   * The data-craft-ignore-events here is key to making
   * drag and drop work inside this label.
   * Becase the label is outside teh component tree, Craft will read a
   * click to this label as a click to a nearby node or Document.
   * hence the data-craft-ignore events to make a click to the label
   * ignore the select event. Note that we haven't added craft only ignores select events.
   */

  return (
    <NodeLabelContainer
      id={`${NODE_LABEL_CONTAINER_ID}-${nodeId}`}
      key={`${nodeId}-${top + left + scrollTop + scrollLeft}`}
      data-craft-ignore-events
      top={offsets.top}
      left={offsets.left}
      hide={!ref.current}
    >
      <NodeLabelMenu
        ref={ref}
        nodeId={nodeId}
        isMappedOverSource={isMappedOverSource}
        onRenderMenu={() => refresh()}
      />
    </NodeLabelContainer>
  );
});
